// @flow

/**
 * Reason Stack Component
 *
 * Renders the running commentary ("reason") supplied by the agent for each
 * in-flight tool call, stacked underneath the EventTimeline status section and
 * above the streamed answer.
 *
 * Behaviour (see AgentMessageBody for the mount gating):
 * - One line per ACTIVE tool call that provided a reason. Parallel tool calls
 *   stack under each other in start order.
 * - When a tool call completes, its reason is removed — but only after a minimum
 *   visible window (MIN_VISIBLE_MS), even if the call finished sooner.
 * - Removal fades + collapses the line (via react-transition-group) so the lines
 *   below slide up to fill the gap.
 * - The whole stack is unmounted instantly (no fade) the moment the answer starts
 *   streaming — that gating lives in AgentMessageBody (`isActive && !hasText`),
 *   so the stack disappears in the same commit the first token is rendered and
 *   never shifts the answer mid-stream.
 *
 * A missing/empty reason is simply skipped — it never renders and never throws.
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

const MIN_VISIBLE_MS = 10000;
const FADE_MS = 500;
// Generous upper bound for the collapse animation; reasons wrap to a few lines.
const MAX_LINE_HEIGHT = '8rem';

const StackContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* Separate the commentary from the status indicators above it */
  margin-top: 1.25rem;
`;

const ReasonLine = styled.div`
  font-size: 0.85rem;
  line-height: 1.35;
  /* Match the agent response text colour (Bubble inherits the app default) */
  color: inherit;
  font-style: italic;
  overflow: hidden;
  /* default (steady) state: a small gap between stacked lines */
  margin-bottom: 0.25rem;

  &.reason-appear,
  &.reason-enter {
    opacity: 0;
  }
  &.reason-appear-active,
  &.reason-enter-active {
    opacity: 1;
    transition: opacity ${FADE_MS}ms ease;
  }
  &.reason-exit {
    opacity: 1;
    max-height: ${MAX_LINE_HEIGHT};
  }
  &.reason-exit-active {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    transition: opacity ${FADE_MS}ms ease, max-height ${FADE_MS}ms ease,
      margin-bottom ${FADE_MS}ms ease;
  }
`;

const Key = (ev) => ev.eventId || ev.id;

const hasReason = (e) =>
  e && typeof e.reason === 'string' && e.reason.trim() !== '';

const ReasonStack = ({ events }: { events: Array<Object> }) => {
  // Items currently rendered (in start order): { key, reason, shownAt }
  const [visible, setVisible] = useState([]);
  // Pending removal timers, keyed by item key
  const timersRef = useRef(new Map());
  // Keys we've ever shown — prevents a faded-out reason re-appearing when a
  // later tool call changes the signature and re-runs the effect.
  const seenRef = useRef(new Set());
  // Latest events / visible list, read inside the effect without re-subscribing.
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Re-run only when the set of reason-bearing events OR their active flag changes,
  // not on every text token (which also clones the events array).
  const signature = (events || [])
    .filter(hasReason)
    .map((e) => `${Key(e)}:${e.withDots ? 1 : 0}`)
    .join('|');

  useEffect(() => {
    const current = (eventsRef.current || []).filter(hasReason);
    const timers = timersRef.current;
    const seen = seenRef.current;
    const now = Date.now();

    // 1) Show any reason we haven't shown yet (active, or — rarely — observed
    //    already-completed), appended in arrival order.
    const additions = [];
    current.forEach((ev) => {
      const key = Key(ev);
      if (!seen.has(key)) {
        seen.add(key);
        additions.push({ key, reason: ev.reason, shownAt: now });
      }
    });
    if (additions.length) {
      setVisible((prev) => [...prev, ...additions]);
    }

    // shownAt for every currently-tracked key (committed visible + new additions).
    const shownAtByKey = new Map(additions.map((a) => [a.key, a.shownAt]));
    visibleRef.current.forEach((r) => {
      if (!shownAtByKey.has(r.key)) shownAtByKey.set(r.key, r.shownAt);
    });

    current.forEach((ev) => {
      const key = Key(ev);
      if (ev.withDots) {
        // Active again: cancel any pending removal.
        if (timers.has(key)) {
          clearTimeout(timers.get(key));
          timers.delete(key);
        }
      } else if (!timers.has(key)) {
        // Completed: remove after the minimum visible window has elapsed.
        const shownAt = shownAtByKey.get(key);
        if (typeof shownAt === 'number') {
          const remaining = Math.max(0, MIN_VISIBLE_MS - (now - shownAt));
          const t = setTimeout(() => {
            timers.delete(key);
            setVisible((cur) => cur.filter((r) => r.key !== key));
          }, remaining);
          timers.set(key, t);
        }
      }
    });
  }, [signature]);

  // Clear all timers on unmount (e.g. when the answer starts streaming and
  // AgentMessageBody stops rendering this component).
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  if (visible.length === 0) return null;

  return (
    <StackContainer aria-live="polite">
      <TransitionGroup component={null}>
        {visible.map((item) => (
          <CSSTransition
            key={item.key}
            timeout={FADE_MS}
            classNames="reason"
            appear
          >
            <ReasonLine>{item.reason}</ReasonLine>
          </CSSTransition>
        ))}
      </TransitionGroup>
    </StackContainer>
  );
};

export default ReasonStack;
